import React, { useEffect, useState } from 'react';
import { useInvoices } from '../../context/InvoicesContext';
import { Building2, Save } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const emptySender = {
  name: '', email: '', address: '',
  companyRegistration: '', vatNumber: '',
  bankName: '', bankAddress: '', accountName: '', accountNumber: '', sortCode: '',
  swift: '', iban: '',
  additional_accounts: [],
};

const emptyAccount = () => ({
  id: 'acc_' + Math.random().toString(36).slice(2, 10),
  label: '',
  bankName: '', bankAddress: '', accountName: '', accountNumber: '', sortCode: '',
  swift: '', iban: '',
});

/**
 * SenderDetails — the sender/business block shown on every invoice.
 * Persisted server-side in db.settings key='sender'. Edited once, used everywhere.
 */
const SenderDetails = () => {
  const { sender, setSender } = useInvoices();
  const [form, setForm] = useState(emptySender);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm({ ...emptySender, ...(sender || {}) });
    setDirty(false);
  }, [sender]);

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await setSender(form);
      toast.success('sender details saved — will appear on all invoices');
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="sender-details-section">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <Building2 className="w-4 h-4 text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-700">your business details</h3>
        <span className="text-xs text-slate-400">— shown on every invoice you send</span>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          size="sm"
          className="ml-auto bg-pink-500 hover:bg-pink-600 text-white disabled:opacity-40"
          data-testid="save-sender-btn"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {saving ? 'saving…' : dirty ? 'save' : 'saved'}
        </Button>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Business name</Label>
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="LVE Social" className="mt-1" data-testid="sender-name" />
        </div>
        <div className="md:col-span-2">
          <Label>Registered address</Label>
          <Textarea value={form.address} onChange={(e) => set('address', e.target.value)} rows={3} placeholder={"Flat 1, 90 Anson Road\nLondon\nNW2 6AG"} className="mt-1 font-mono text-sm" data-testid="sender-address" />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="bec@lvesocial.com" className="mt-1" data-testid="sender-email" />
        </div>
        <div>
          <Label>Company registration</Label>
          <Input value={form.companyRegistration} onChange={(e) => set('companyRegistration', e.target.value)} placeholder="13437642" className="mt-1 font-mono" data-testid="sender-company-reg" />
        </div>
        <div>
          <Label>VAT number <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Input value={form.vatNumber} onChange={(e) => set('vatNumber', e.target.value)} placeholder="482353288" className="mt-1 font-mono" data-testid="sender-vat" />
        </div>
        <div /* spacer */ />

        <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-medium mb-3">bank details</div>
        </div>
        <div>
          <Label>Bank name</Label>
          <Input value={form.bankName} onChange={(e) => set('bankName', e.target.value)} placeholder="Tide" className="mt-1" />
        </div>
        <div>
          <Label>Account name</Label>
          <Input value={form.accountName} onChange={(e) => set('accountName', e.target.value)} placeholder="LVE Social" className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <Label>Bank address</Label>
          <Input value={form.bankAddress} onChange={(e) => set('bankAddress', e.target.value)} placeholder="4th Floor, The Featherstone Building, 66 City Road, EC1Y 2AL" className="mt-1" />
        </div>
        <div>
          <Label>Sort code</Label>
          <Input value={form.sortCode} onChange={(e) => set('sortCode', e.target.value)} placeholder="04-06-05" className="mt-1 font-mono" />
        </div>
        <div>
          <Label>Account number</Label>
          <Input value={form.accountNumber} onChange={(e) => set('accountNumber', e.target.value)} placeholder="16098584" className="mt-1 font-mono" />
        </div>
        <div>
          <Label>SWIFT / BIC</Label>
          <Input value={form.swift} onChange={(e) => set('swift', e.target.value)} placeholder="CLRBGB22" className="mt-1 font-mono" />
        </div>
        <div>
          <Label>IBAN</Label>
          <Input value={form.iban} onChange={(e) => set('iban', e.target.value)} placeholder="GB47 CLRB 0406 0516 0985 84" className="mt-1 font-mono" />
        </div>
      </div>
    </div>
  );
};

export default SenderDetails;
